import { useEffect, useState } from "react";
import { supabase } from "@/integrations/supabase/client";

export const useThemePreferences = () => {
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    loadPreferences();

    // Listen for auth state changes
    const { data: { subscription } } = supabase.auth.onAuthStateChange(() => {
      loadPreferences();
    });

    return () => subscription.unsubscribe();
  }, []);

  const loadPreferences = async () => {
    try {
      const { data: { user } } = await supabase.auth.getUser();
      
      if (!user) {
        // Load from localStorage if not authenticated
        applyStoredPreferences();
        setLoading(false);
        return;
      }

      // First apply stored preferences immediately for faster initial load
      const storedTheme = localStorage.getItem("theme_mode");
      if (storedTheme) {
        applyTheme(storedTheme);
      }

      // Fetch from database
      const { data, error } = await supabase
        .from("user_preferences")
        .select("theme_mode, accent_color, font_size, field_readability")
        .eq("user_id", user.id)
        .single();

      if (error && error.code !== "PGRST116") {
        console.error("Error loading preferences:", error);
        applyStoredPreferences();
      } else if (data) {
        applyTheme(data.theme_mode);
        applyAccentColor(data.accent_color);
        applyFontSize(data.font_size);
        applyFieldReadability(data.field_readability);
        
        // Store in localStorage for persistence
        localStorage.setItem("theme_mode", data.theme_mode);
        localStorage.setItem("accent_color", data.accent_color);
        localStorage.setItem("font_size", data.font_size);
        localStorage.setItem("field_readability", data.field_readability.toString());
      } else {
        applyStoredPreferences();
      }
    } catch (error) {
      console.error("Error in loadPreferences:", error);
      applyStoredPreferences();
    } finally {
      setLoading(false);
    }
  };

  const applyStoredPreferences = () => {
    const themeMode = localStorage.getItem("theme_mode") || "Light";
    const accentColor = localStorage.getItem("accent_color") || "#D6B77A";
    const fontSize = localStorage.getItem("font_size") || "Medium";
    const fieldReadability = localStorage.getItem("field_readability") === "true";

    applyTheme(themeMode);
    applyAccentColor(accentColor);
    applyFontSize(fontSize);
    applyFieldReadability(fieldReadability);
  };

  const applyTheme = (mode: string) => {
    console.log("Applying theme mode:", mode);
    
    if (mode === "Dark") {
      document.documentElement.classList.add("dark");
      localStorage.setItem("theme_mode", "Dark");
    } else if (mode === "Light") {
      document.documentElement.classList.remove("dark");
      localStorage.setItem("theme_mode", "Light");
    } else {
      // Auto mode - use system preference but don't override stored preference
      const prefersDark = window.matchMedia("(prefers-color-scheme: dark)").matches;
      if (prefersDark) {
        document.documentElement.classList.add("dark");
      } else {
        document.documentElement.classList.remove("dark");
      }
      localStorage.setItem("theme_mode", "Auto");
    }
  };

  const applyFontSize = (size: string) => {
    const root = document.documentElement;
    switch (size) {
      case "Small":
        root.style.fontSize = "14px";
        break;
      case "Large":
        root.style.fontSize = "18px";
        break;
      default:
        root.style.fontSize = "16px";
    }
  };

  const applyAccentColor = (color: string) => {
    document.documentElement.style.setProperty("--accent-custom", color);
  };

  const applyFieldReadability = (enabled: boolean) => {
    if (enabled) {
      document.documentElement.classList.add("field-readability");
    } else {
      document.documentElement.classList.remove("field-readability");
    }
  };

  return { loading };
};
